import { Injectable, signal } from '@angular/core';

export type ToastType = 'success' | 'error' | 'warning' | 'info' | 'confirm';

export interface ToastConfirmOptions {
  title?: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  confirmStyle?: 'success' | 'danger' | 'warning' | 'primary';
  duration?: number;
}

export interface Toast {
  id: string;
  type: ToastType;
  message: string;
  title?: string;
  code?: string;
  details?: string[];
  duration?: number;
  confirmText?: string;
  cancelText?: string;
  confirmStyle?: 'success' | 'danger' | 'warning' | 'primary';
  resolvePromise?: (confirmed: boolean) => void;
}

@Injectable({
  providedIn: 'root'
})
export class ToastService {
  toasts = signal<Toast[]>([]);
  private lastToastKey = '';
  private lastToastTime = 0;

  show(
    message: string,
    type: 'success' | 'error' | 'warning' | 'info' = 'info',
    duration: number = 6000,
    code?: string,
    details?: string[]
  ) {
    // Deduplicate identical toasts arriving within 500ms
    const toastKey = `${type}:${message}:${code || ''}:${JSON.stringify(details || [])}`;
    const now = Date.now();
    if (this.lastToastKey === toastKey && now - this.lastToastTime < 500) {
      return;
    }
    this.lastToastKey = toastKey;
    this.lastToastTime = now;

    const id = Math.random().toString(36).substring(2, 9);
    const toast: Toast = { id, message, type, duration, code, details };
    this.toasts.update(items => [...items, toast]);

    setTimeout(() => {
      this.remove(id);
    }, duration);
  }

  confirm(options: ToastConfirmOptions): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      const id = Math.random().toString(36).substring(2, 9);
      const duration = options.duration || 15000;

      let resolved = false;
      let timer: any = null;

      const safeResolve = (value: boolean) => {
        if (resolved) return;
        resolved = true;
        if (timer) clearTimeout(timer);
        this.toasts.update(items => items.filter(t => t.id !== id));
        resolve(value);
      };

      const toast: Toast = {
        id,
        type: 'confirm',
        title: options.title || 'Are you sure?',
        message: options.message,
        confirmText: options.confirmText || 'Yes, Proceed',
        cancelText: options.cancelText || 'Cancel',
        confirmStyle: options.confirmStyle || 'primary',
        duration,
        resolvePromise: safeResolve
      };

      this.toasts.update(items => [...items, toast]);

      timer = setTimeout(() => {
        safeResolve(false);
      }, duration);
    });
  }

  handleConfirm(toast: Toast) {
    if (toast.resolvePromise) {
      toast.resolvePromise(true);
    } else {
      this.remove(toast.id);
    }
  }

  handleCancel(toast: Toast) {
    if (toast.resolvePromise) {
      toast.resolvePromise(false);
    } else {
      this.remove(toast.id);
    }
  }

  success(message: string, code?: string) {
    this.show(message, 'success', 5000, code);
  }

  error(message: string, code?: string, details?: string[]) {
    this.show(message, 'error', 7000, code, details);
  }

  warning(message: string, code?: string, details?: string[]) {
    this.show(message, 'warning', 6000, code, details);
  }

  info(message: string, code?: string) {
    this.show(message, 'info', 4000, code);
  }

  remove(id: string) {
    const target = this.toasts().find(t => t.id === id);
    if (target?.resolvePromise) {
      target.resolvePromise(false);
    } else {
      this.toasts.update(items => items.filter(t => t.id !== id));
    }
  }
}
